import { computed } from 'vue'
import { ysAlerts } from '@onxmaps/yellowstone-ui/alerts'
import { captureException } from '@sentry/vue'

import { getApolloClient } from '@/lib/apollo-client'
import Geographic, { bboxFromVisibleRegion } from '@/lib/geographic'
import { getWaterbodyIDsByBbox } from '@/fish/lib/graph/queries/waterbodies-gql'
import { FISH_LAYERS, XGPS_STYLESHEET_LAYER_ID } from '@/fish/lib/enums'
import {
  buildFiltersForRequest,
  buildFishingAreaMapboxFilter,
  buildCatchDataMapboxFilter,
} from '@/fish/lib/filtering'
import { determineSpeciesToFilter } from '@/fish/lib/filtering/filter-builders'
import { all } from '@/services/viewer-filter'
import Camera from '@/lib/camera'
import type MapFeature from '@/lib/map-feature'

import {
  visibleRegion,
  setFeaturesFiltered,
  camera,
  setDestination,
  setFilters,
} from '@/services/map-service'
import { hideLayers, showLayers } from '@/services/layer-management-service'
import layerService from '@/services/layer-service'
import marketingEventService from '@/services/marketing-event-service'

import type { WaterbodyId } from '@/fish/types'
import type { Snackbar } from '@/types/types'

let snackbar: null | Snackbar = null

const FISHING_AREA_UNFILTERED_LAYERS = [
  XGPS_STYLESHEET_LAYER_ID.FISHING_AREAS_POINTS,
  XGPS_STYLESHEET_LAYER_ID.FISHING_AREAS_NAMES,
  XGPS_STYLESHEET_LAYER_ID.FISHING_AREAS_DOTS,
]
const FISHING_AREA_FILTERED_LAYER =
  XGPS_STYLESHEET_LAYER_ID.FISHING_AREAS_FILTERED

// Unlike fishing areas, catch data has no dedicated filtered-fill layer in the
// stylesheet. The single fill layer is therefore visible in BOTH the unfiltered
// and filtered states, and only its filter expression toggles. That is why the
// fill's filter must be reset to match-all when filters clear (see resetFilters).
const CATCH_DATA_PERSISTENT_LAYER = XGPS_STYLESHEET_LAYER_ID.CATCH_DATA_FILL
// Border shown only when NOT filtering
const CATCH_DATA_UNFILTERED_LAYERS = [
  XGPS_STYLESHEET_LAYER_ID.CATCH_DATA_BORDER,
]
// Border shown only when filtering
const CATCH_DATA_FILTERED_LAYERS = [
  XGPS_STYLESHEET_LAYER_ID.CATCH_DATA_BORDER_FILTERED,
]
// Layers that receive the catch data filter expression
const CATCH_DATA_FILTER_TARGETS = [
  CATCH_DATA_PERSISTENT_LAYER,
  ...CATCH_DATA_FILTERED_LAYERS,
]

const isFishingAreasLayerEnabled = () =>
  layerService.getLayerPreference('fishing_areas')?.active !== false

/**
 * Reset the filter expression on the given layers to match-all.
 * A falsy filter is a no-op in layer-management, so all() (['all']) is used to
 * actually overwrite any stale filter and make every feature render again.
 */
const resetFilters = (layerIds: string[]) => {
  if (layerIds.length === 0) {
    return
  }
  const layerFilters = new Map<string, unknown[]>()
  layerIds.forEach((layerId) => layerFilters.set(layerId, all()))
  setFilters(layerFilters)
}

/**
 * Check if all filter expressions are empty, indicating no filters are active
 */
export const filterExpressionsAreEmpty = (
  response: Record<string, string>
): boolean => {
  return !Object.values(response).some((value) => value !== '')
}

/**
 * Adjust camera to help users see results
 * Results render at zooms > 6
 * If they are at a zoom where results don't render, zoom in to an appropriate level
 */
export const setCameraHeight = () => {
  const currentCamera = camera()
  if (currentCamera?.location) {
    const { latitude, longitude, height } = currentCamera.location
    if (height < 6) {
      const newCamera = new Camera({
        ...currentCamera,
        location: new Geographic(longitude, latitude, 6.5),
      })
      setDestination(newCamera)
    }
  }
}

/**
 * Control display of map pins to represent waterbodies that meet filter criteria
 * Query graph for list of waterbodies, then set state on corresponding map feature
 * Every interaction with a filter, or Search this Area will run this code
 */
export async function applyMapFilteringDisplay() {
  const client = getApolloClient()
  const bbox = computed(() => bboxFromVisibleRegion(visibleRegion()))
  const queryFilters = buildFiltersForRequest()

  // reset snackbar each iteration
  snackbar && ysAlerts.cancelAlert(snackbar)

  setCameraHeight()

  // If no filters in use, clear pins, turn on lake badge layer, reset fishing area layers
  if (filterExpressionsAreEmpty(queryFilters)) {
    setFeaturesFiltered([])
    showLayers(['water_special$lake_badge'])
    if (isFishingAreasLayerEnabled()) {
      showLayers([
        ...FISHING_AREA_UNFILTERED_LAYERS,
        CATCH_DATA_PERSISTENT_LAYER,
        ...CATCH_DATA_UNFILTERED_LAYERS,
      ])
    }
    hideLayers([FISHING_AREA_FILTERED_LAYER, ...CATCH_DATA_FILTERED_LAYERS])
    // The persistent fill stays visible, so clear its stale filter expression.
    resetFilters([CATCH_DATA_PERSISTENT_LAYER])
  } else {
    try {
      //  Query for waterbody IDs meeting filter criteria
      const { data, errors } = await client.query({
        query: getWaterbodyIDsByBbox(queryFilters),
        variables: {
          bottom: bbox.value?.bottom,
          left: bbox.value?.left,
          right: bbox.value?.right,
          top: bbox.value?.top,
          limit: 100,
        },
      })
      if (errors?.length) {
        captureException(errors, {
          tags: {
            context: 'map-pin-filtering',
          },
        })
      }

      // Transform response to map feature
      const waterbodyEdges: WaterbodyId[] = data?.waterbodyIDs
      const waterbodiesToFilter = waterbodyEdges?.map(
        (waterbody: WaterbodyId) =>
          ({
            id: waterbody.id,
            source: FISH_LAYERS.WATERBODY_LAYER,
            sourceLayer: FISH_LAYERS.POI_SOURCE,
          }) as MapFeature
      )
      applyAreaFiltersDisplay(waterbodyEdges)
      applyCatchDataFiltersDisplay(waterbodyEdges)
      applyWaterbodyFilterDisplay(waterbodiesToFilter)
    } catch {
      // Fall back to turning off pins and turning on lake badge layer
      setFeaturesFiltered([])
      showLayers(['water_special$lake_badge'])
    }
  }
}

const applyWaterbodyFilterDisplay = (
  waterbodiesToFilter: MapFeature[] | undefined
) => {
  if (waterbodiesToFilter) {
    setFeaturesFiltered(waterbodiesToFilter)
    snackbar && ysAlerts.cancelAlert(snackbar)
    hideLayers(['water_special$lake_badge'])
  } else {
    setFeaturesFiltered([])
    snackbar = ysAlerts.enqueueAlert({
      alertType: 'snackbar',
      dismissible: true,
      text: 'No results, try adjusting your zoom or changing your filters.',
    })
    marketingEventService.publish('map_filter_no_results')
  }
}

const applyAreaFiltersDisplay = (waterbodyIds: WaterbodyId[] | undefined) => {
  // Zero results -> hide all fishing area layers
  if (!waterbodyIds || waterbodyIds.length === 0) {
    hideLayers([...FISHING_AREA_UNFILTERED_LAYERS, FISHING_AREA_FILTERED_LAYER])
    return
  }

  const selectedSpecies = determineSpeciesToFilter()

  // No species selected -> show unfiltered layers, no filter needed
  if (selectedSpecies.length === 0) {
    if (isFishingAreasLayerEnabled()) {
      showLayers(FISHING_AREA_UNFILTERED_LAYERS)
    }
    hideLayers([FISHING_AREA_FILTERED_LAYER])
    return
  }

  const kgIds = waterbodyIds.map((edge) => edge.id)
  const layerFilters = new Map<string, unknown[]>()
  layerFilters.set(
    FISHING_AREA_FILTERED_LAYER,
    buildFishingAreaMapboxFilter(kgIds, selectedSpecies)
  )
  setFilters(layerFilters)

  if (isFishingAreasLayerEnabled()) {
    showLayers([FISHING_AREA_FILTERED_LAYER])
    // Delay hiding unfiltered layers to prevent a flash of the filtered layer
    // rendering on top of an unfiltered feature before the swap completes
    setTimeout(() => {
      hideLayers(FISHING_AREA_UNFILTERED_LAYERS)
    }, 100)
  }
}

const applyCatchDataFiltersDisplay = (
  waterbodyIds: WaterbodyId[] | undefined
) => {
  // Zero results -> hide all catch data layers
  if (!waterbodyIds || waterbodyIds.length === 0) {
    hideLayers([
      CATCH_DATA_PERSISTENT_LAYER,
      ...CATCH_DATA_UNFILTERED_LAYERS,
      ...CATCH_DATA_FILTERED_LAYERS,
    ])
    return
  }

  const selectedSpecies = determineSpeciesToFilter()

  // No species selected -> show unfiltered view, no filter needed
  if (selectedSpecies.length === 0) {
    if (isFishingAreasLayerEnabled()) {
      showLayers([CATCH_DATA_PERSISTENT_LAYER, ...CATCH_DATA_UNFILTERED_LAYERS])
    }
    hideLayers(CATCH_DATA_FILTERED_LAYERS)
    // The persistent fill stays visible, so clear its stale filter expression.
    resetFilters([CATCH_DATA_PERSISTENT_LAYER])
    return
  }

  const kgIds = waterbodyIds.map((edge) => edge.id)
  const catchDataFilter = buildCatchDataMapboxFilter(kgIds, selectedSpecies)
  const layerFilters = new Map<string, unknown[]>()
  CATCH_DATA_FILTER_TARGETS.forEach((layerId) =>
    layerFilters.set(layerId, catchDataFilter)
  )
  setFilters(layerFilters)

  if (isFishingAreasLayerEnabled()) {
    showLayers([CATCH_DATA_PERSISTENT_LAYER, ...CATCH_DATA_FILTERED_LAYERS])
    // Delay hiding unfiltered layers to prevent a flash of the filtered layer
    // rendering on top of an unfiltered feature before the swap completes
    setTimeout(() => {
      hideLayers(CATCH_DATA_UNFILTERED_LAYERS)
    }, 100)
  }
}
